<template>
  <div v-if="!ready" v-loading="!ready" element-loading-text="拼命加载中" class="page-loading"></div>
  <div v-else>
    <h3 class="m-t-0">{{$query.plan_uid ? '编辑' : '添加'}}计划</h3>
    <el-form :model="form" :rules="rules" ref="form" label-width="6em" label-position="left">
      <el-form-item label="学年">
        {{form.term}}学年
      </el-form-item>
      <el-form-item label="计划类型" prop="type">
        <el-select v-model="form.type" class="full-width">
          <el-option v-for="type in plan_types" :key="type.id"
                     :label="type.name" :value="type.id"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="任教年级" prop="grade" required>
        <el-cascader expand-trigger="hover" :options="grade_options" :value="[this.form.level, this.form.grade]"
                     @change="onGradeChange" class="full-width">
        </el-cascader>
      </el-form-item>
      <el-form-item label="任教学科" prop="subject">
        <el-select v-model="form.subject" class="full-width" filterable placeholder="选择年级后选择学科"
                   :filter-method="filterSubject" @visible-change="filterSubject('')">
          <el-option v-for="subject in filtered_subjects" :key="subject.name"
                     :label="subject.name" :value="subject.name">
            <span v-html="$highlight(subject.name, subject.$$pinyin.name)"></span>
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="任教班级" prop="group">
        <el-input v-model="form.group"></el-input>
      </el-form-item>
      <el-form-item label="提交部门" prop="department_uid">
        <el-select v-model="form.department_uid" class="full-width">
          <el-option v-for="department in departments" :key="department.uid"
                     :label="department.name" :value="department.uid"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="附件" prop="file">
        <select-file :value="form.file" @change="form.file = $event; $refs.form.validateField('file')">
          <el-button size="small" type="primary">添加文件</el-button>
        </select-file>
        <span class="el-upload__tip">
          <span>鼠标移动到文件上点击<i class="el-icon-close text-danger f-s-0_9"></i>可删除</span>
        </span>
        <ul class="el-upload-list">
          <li v-for="(file, index) in form.file"
              class="el-upload-list__item" :key="index">
            <a class="el-upload-list__item-name" :href="file.url" download target="_blank">
              <icon :name="file.name | fileicon"></icon>
              {{file.name || '(未命名)'}}
            </a>
            <i class="el-icon-close text-danger" @click="form.file.splice(index, 1)"></i>
          </li>
        </ul>
      </el-form-item>
      <div class="text-center m-v-10">
        <el-button type="success" size="cons" @click="submit()" :loading="submit_loading > 0">
          提交
        </el-button>
        <el-button type="default" size="cons" @click="$router.back()" :loading="submit_loading > 0">
          返回
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import assign from 'lodash/assign'
import find from 'lodash/find'
import orderBy from 'lodash/orderBy'

import * as Plan from 'api/plan'
import * as Department from 'api/department'
import * as Subject from 'api/configuration/subject'

import term from 'utils/term'
import grades from 'constants/grades'
import plan_types from 'constants/plan_types'

export default {
  data () {
    return {
      ready: false,
      form: null,
      submit_loading: 0,
      rules: {
        type: [this.$rules.required({message: '计划类型为必填项', type: 'number'})],
        subject: [this.$rules.required('任教学科为必填项')],
        department_uid: [this.$rules.required('提交部门为必填项')],
        file: [this.$rules.required({message: '请添加文件', type: 'array'})],
      },
      subjects: null,
      search: '',
    }
  },
  computed: {
    grade_subjects () {
      if (!this.form.level) return []
      return this.subjects.filter(subject => subject.name.indexOf(this.form.level) === 0)
    },
    filtered_subjects () {
      return this.$search(this.grade_subjects, this.search, ['name'])
    },
  },
  methods: {
    init () {
      this.ready = false
      this.$guard()(async () => {
        let [plan, subjects, departments] = await Promise.all([
          this.$query.plan_uid ? Plan.get({uid: this.$query.plan_uid}) : null,
          Subject.list(),
          Department.list(),
        ])
        this.grades = grades
        if (plan) {
          let form = assign({uid: this.$query.plan_uid}, plan)
          if (!form.level) {
            let grade = find(grades, i => i.grades.includes(form.grade))
            form.level = grade.level
          }
          form.group = form.group || ''
          form.type = form.type || null
          form.department_uid = form.department_uid || ''
          this.form = form
        } else {
          this.form = {
            term: term(),
            level: '',
            grade: '',
            subject: '',
            group: '',
            file: [],
            type: null,
            department_uid: '',
          }
        }
        this.plan_types = plan_types
        this.grade_options = grades.map(grade => {
          return {
            label: grade.level,
            value: grade.level,
            children: grade.grades.map(grade => ({label: grade, value: grade})),
          }
        })
        // TODO 教务处、教育处排在前面
        this.departments = orderBy(departments, function (department) {
          if (department.name === '教务处') return -2
          if (department.name === '教育处') return -1
          return 0
        })
        this.subjects = subjects.map(name => ({name}))
        this.ready = true
      })
    },
    async submit () {
      if (!await this.$validate(this.$refs.form)) return
      this.$guard(this, 'submit_loading')(async () => {
        await Plan.edit(this.form)
        this.$message.success('提交成功')
        this.$router.back()
      })
    },
    filterSubject (query) {
      this.search = query
    },
    onGradeChange (val) {
      val = val || []
      if (this.form.level !== val[0]) {
        let old_level = this.form.level
        this.form.level = val[0]
        let new_subject = this.form.level + this.form.subject.substring(old_level.length)
        this.form.subject = find(this.grade_subjects, i => i.name === new_subject) ? new_subject : ''
      }
      this.form.grade = val[1]
    },
  },
  created () {
    this.init()
  },
  watch: {
    $route () { if (!this.$dontReload) this.init() },
  },
}
</script>

<style lang="scss" scoped>
@import "src/scss/var/colors";

.el-form {
  max-width: 600px;
}

.el-upload-list {
  max-width: 300px;
  .el-upload-list__item {
    margin: 0;
    padding: 2px 0;
    .el-icon-close {
      color: $color-danger;
      font-size: 1.5em;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/plan/PlanEdit.vue